import { customerIdZodSchema } from "@@/shared/users/customer-id";
import { systemUserZodSchema } from "@@/shared/users/system-user";
import { userIdZodSchema } from "@@/shared/users/user-id";

import { isoAndUnixZodSchema } from "@@/shared/date-time/iso-and-unix";
import { contactInfoZodObject } from "@@/shared/locations/contact-info";
import { priceZodSchema } from "@@/shared/prices/price";
import { z } from "zod";
import { payRequestReferencesZodSchema } from "../../events/_pay-request-references";
import { payRequestPaymentCancelledEventZodObject } from "../../events/pay-request.payment-cancelled.event";
import { payRequestPaymentExpiredEventZodObject } from "../../events/pay-request.payment-expired.event";
import { payRequestPaymentFailedEventZodObject } from "../../events/pay-request.payment-failed.event";
import { payRequestPaymentFulfilledEventZodObject } from "../../events/pay-request.payment-fulfilled.event";
import { payRequestPaymentInitiatedEventZodObject } from "../../events/pay-request.payment-initiated.event";
import { payRequestIdZodSchema } from "../../pay-request-id";
import { payRequestItemZodSchema } from "../../pay-request-item";

const payRequestForPayerProjectionStatusZodObject = z.object({
    _type: z.literal("PAY_REQUEST_FOR_PAYER_PROJECTION_STATUS"),
    name: z.enum([
        "cancelled",
        "expired",
        "fulfilled",
        "pending",
        "pending-payment",
    ]),
    reason: z.string().optional(),
    by: userIdZodSchema
        .or(customerIdZodSchema)
        .or(systemUserZodSchema)
        .or(z.literal("PAYMENT_PROVIDER"))
        .or(z.literal("UNKNOWN")),
});

type PayRequestForPayerProjectionStatus = z.infer<
    typeof payRequestForPayerProjectionStatusZodObject
>;
const payRequestForPayerProjectionStatusZodSchema: z.ZodSchema<PayRequestForPayerProjectionStatus> =
    payRequestForPayerProjectionStatusZodObject;

const payRequestForPayerProjectionStatusFactory = (params: {
    readonly status: PayRequestForPayerProjectionStatus["name"];
    readonly by?: PayRequestForPayerProjectionStatus["by"];
}): PayRequestForPayerProjectionStatus => {
    const by = params.by ?? "UNKNOWN";
    return payRequestForPayerProjectionStatusZodSchema.parse({
        _type: "PAY_REQUEST_FOR_PAYER_PROJECTION_STATUS",
        name: params.status,
        by,
    });
};

const payRequestForPayerProjectionZodObject = z.object({
    _id: payRequestIdZodSchema,
    _type: z.literal("PAY_REQUEST_FOR_PAYER_PROJECTION"),
    _createdAt: isoAndUnixZodSchema,
    _version: z.number(),
    status: payRequestForPayerProjectionStatusZodSchema,
    paymentInitiated: z.boolean(),
    paymentFulfilled: z.boolean(),
    orderContactInfo: contactInfoZodObject,
    /**
     * When someone initiates an attempt to pay the pay request,
     * payment events are created to follow the payment process(es)
     * E.g. the first attempt could fail, then the payer could try again
     * and more payment events would be created
     */
    paymentEvents: z.array(
        z.union([
            payRequestPaymentCancelledEventZodObject,
            payRequestPaymentFailedEventZodObject,
            payRequestPaymentFulfilledEventZodObject,
            payRequestPaymentExpiredEventZodObject,
            payRequestPaymentInitiatedEventZodObject,
        ]),
    ),
    /** Items to be paid using the pay request */
    items: z.array(payRequestItemZodSchema).min(1),
    /** Pay request amount */
    price: priceZodSchema,
    replacedBy: payRequestIdZodSchema.optional(),
    /**
     * An object with references to the order, order group, order events and provider that the
     * pay request is a payment for
     * */
    paymentFor: payRequestReferencesZodSchema,
});

type PayRequestForPayerProjection = z.infer<
    typeof payRequestForPayerProjectionZodObject
>;

export {
    payRequestForPayerProjectionStatusFactory,
    payRequestForPayerProjectionStatusZodObject,
    payRequestForPayerProjectionStatusZodSchema,
    payRequestForPayerProjectionZodObject,
};
export type {
    PayRequestForPayerProjection,
    PayRequestForPayerProjectionStatus,
};
